import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import VoiceRecorder from "./VoiceRecorder.jsx";

// Section B, "React if you want".
//
// Optional by construction: no XP, no badge, no ring, and nothing here counts toward
// the progress percentage. The only number on the page is a grey "n of m reacted".
// Copy is English throughout — Romain reads and publishes in English (brief, Langue).

const DEFAULT_VERDICTS = {
  post: [
    "I could do this, and I want to",
    "I could do this, but I don't want to",
    "Not me",
    "Not sure",
  ],
  mirror: ["That's me", "Partly", "Not how I'd put it"],
  analysis: ["That's right", "Close, but", "No"],
  number: ["Right", "Wrong", "Needs context"],
  claim: ["That's right", "Close, but", "No"],
};

const verdictsFor = (item) =>
  (Array.isArray(item.verdict_set) && item.verdict_set.length && item.verdict_set) ||
  DEFAULT_VERDICTS[item.kind] ||
  DEFAULT_VERDICTS.post;

export default function ReactSection({ items, responses, token, onBack, reload }) {
  const byItem = Object.fromEntries((responses || []).map((r) => [r.item_id, r]));
  const touched = (responses || []).filter((r) => r.verdict || r.note_text || r.audio_key || r.skipped).length;

  // Preserve the operator's grouping and order; don't re-sort by category name.
  const groups = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.category === it.category) last.items.push(it);
    else groups.push({ category: it.category, items: [it] });
  }

  return (
    <div className="react">
      <div className="quest__nav">
        <button className="link-btn" onClick={onBack}>← Back to the dashboard</button>
        <span className="react__count">{touched} of {items.length} reacted</span>
      </div>

      <div className="react__head">
        <h1 className="qf-title">React if you want</h1>
        <p className="qf-lead">
          Nothing here is required before Tuesday. If you have ten minutes and feel like
          reacting out loud, the voice button is there. If not, we'll go through it
          together on the 4th.
        </p>
        <button className="link-btn" onClick={onBack}>
          Skip this whole section, we'll do it Tuesday
        </button>
      </div>

      {groups.map((g) => (
        <section className="react__group" key={g.category}>
          <div className="section__label">{g.category}</div>
          <div className="qf-stack--lg react__cards">
            {g.items.map((it) => (
              <ReactCard key={it.id} item={it} response={byItem[it.id]} token={token} reload={reload} />
            ))}
          </div>
        </section>
      ))}

      <div className="quest__foot">
        <button className="qf-btn qf-btn--primary" onClick={onBack}>Back to the dashboard</button>
      </div>
    </div>
  );
}

function ReactCard({ item, response, token, reload }) {
  const [verdict, setVerdict] = useState(response?.verdict || null);
  const [note, setNote] = useState(response?.note_text || "");
  const [skipped, setSkipped] = useState(!!response?.skipped);
  const [saved, setSaved] = useState(false);
  const [audioSaved, setAudioSaved] = useState(!!response?.audio_key);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = () => { setSaved(true); setTimeout(() => setSaved(false), 1600); };

  async function send(payload) {
    try { await api.react({ token, item_id: item.id, ...payload }); flash(); }
    catch { /* the participant sees the unsaved state; nothing is lost locally */ }
  }

  const pick = (v) => {
    const next = verdict === v ? null : v;
    setVerdict(next); setSkipped(false);
    send(next ? { verdict: next } : { action: "clear" });
    if (!next) { setNote(""); setAudioSaved(false); }
  };

  const onNote = (e) => {
    const v = e.target.value;
    setNote(v); setSkipped(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (v.trim()) send({ note_text: v }); }, 700);
  };

  const skip = () => { setSkipped(true); send({ action: "skip" }); };
  const unskip = () => { setSkipped(false); send({ action: "clear" }); };

  return (
    <article className={`ritem ${skipped ? "ritem--skipped" : ""}`}>
      <div className="ritem__eyebrow">{item.category}</div>

      <div className="ritem__head">
        {item.title && <h2 className="ritem__title">{item.title}</h2>}
        {item.author && <div className="ritem__author">{item.author}</div>}
      </div>

      {item.excerpt && <blockquote className="ritem__excerpt">{item.excerpt}</blockquote>}

      {item.url && (
        <a className="ritem__link" href={item.url} target="_blank" rel="noreferrer">
          Open the original ↗
        </a>
      )}

      {item.operator_note && <p className="ritem__note">{item.operator_note}</p>}

      {!skipped && (
        <>
          <div className="ritem__verdicts">
            {verdictsFor(item).map((v) => (
              <button key={v} type="button"
                className={`choice ${verdict === v ? "choice--on" : ""}`}
                onClick={() => pick(v)}>{v}</button>
            ))}
          </div>

          {/* Voice first, then the typed fallback — same order as the `voice` question. */}
          <div className="field">
            <VoiceRecorder token={token} quest_key="react" question_key={`react_${item.id}`}
              onUploaded={async ({ key, mime }) => {
                await api.react({ token, item_id: item.id, audio_r2_key: key, mime });
                setAudioSaved(true); flash(); reload && reload();
              }} />
            {audioSaved && !saved && <p className="muted-sm">Voice note saved.</p>}
            <p className="muted-sm" style={{ marginTop: "var(--qf-2)" }}>Or type it, if that's quicker.</p>
            <textarea className="input" value={note} onChange={onNote}
              placeholder="Anything you want to add…" />
          </div>
        </>
      )}

      <div className="actions">
        {skipped
          ? <button className="subtle" onClick={unskip}>React after all</button>
          : <button className="subtle" onClick={skip}>Skip this one</button>}
        {saved && <span className="saved"><b>✓</b> Saved</span>}
      </div>
    </article>
  );
}
